import * as React from 'react';
import { useEffect, useContext, useState } from 'react';
import {
  Container,
  Typography,
  useTheme,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useTranslation } from 'react-i18next';

import { UserContext } from '../store/userContext';
import { SiteContext } from '../store/siteContext';
import { ReportContext } from '../store/reportContext';
import useGetSiteName from '../utils/hooks/useGetSiteName';
import loadingUsers from './loadingUsers';

import UserHeader from './UserHeader';
import UserColumn from './UserColumn';
import SiteColumn from './SiteColumn';
import StorageColumn from './StorageColumn';
import ReportTypeColumn from './ReportTypeColumn';

const UserList: React.FC = () => {
  const {
    getUsers,
    users,
    getUsersLoading,
  } = useContext(UserContext);
  const { getReports, reports, getReportsLoading } = useContext(ReportContext);
  const { getSite } = useContext(SiteContext);
  const { getSiteName } = useGetSiteName();
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const theme = useTheme();
  const { t } = useTranslation('nav');


    useEffect(() => {
    getUsers();
  }, [getUsers]);

  const filteredUsers = (getUsersLoading ? loadingUsers : users).filter((user) =>
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleUserClick = (user: any) => {
    setSelectedUserId(user.id);
    setSelectedSiteId(null);
    user.siteIds?.forEach((siteId: number) => getSite(siteId));
  };

  const handleSiteClick = (siteId: number) => {
    setSelectedSiteId(siteId);
    if (selectedUserId) {
      getReports(false, { userId: selectedUserId, siteId });
    }
  };

  const selectedUser = users.find((u) => u.id === selectedUserId);

  return (
    <Container maxWidth="lg">
      <Typography
        align="left"
        variant="h3"
        sx={(theme) => ({
          ...theme.typography.listTitle,
          color: theme.palette.titleColor,
          [theme.breakpoints.down('md')]: {
            fontSize: '2.5rem',
          },
        })}
      >
        {t('t.users')}
      </Typography>
      <Grid container alignItems="stretch" sx={{ border: theme.palette.border }}>
        <UserHeader
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          selectedUser={selectedUser}
        />

        <Grid container size={{ xs: 12 }} alignItems="stretch">
          <UserColumn
            filteredUsers={filteredUsers}
            selectedUserId={selectedUserId}
            onUserClick={handleUserClick}
          />

          <SiteColumn
            selectedUserId={selectedUserId}
            selectedSiteId={selectedSiteId}
            siteIds={selectedUser?.siteIds}
            getSiteName={getSiteName}
            onSiteClick={handleSiteClick}
          />

          <StorageColumn
            selectedUserId={selectedUserId}
            selectedSiteId={selectedSiteId}
            reports={reports}
          />

          <ReportTypeColumn
            selectedUserId={selectedUserId}
            getReportsLoading={getReportsLoading}
            reports={reports}
          />
        </Grid>
      </Grid>
    </Container>
  );
};

export default UserList;
